// === WT4 finance (§13): раздел «Финансы → Требуют внимания» ===
// GET /api/v1/finance/attention/ — вычисляемые строки по категориям; запись
// исчезает после устранения причины (не хранимая очередь).
import { finance } from '../../api/endpoints';
import type { FinAttentionRow, FinanceAttention } from '../../api/types';
import {
    Card, Column, DataTable, ErrorBox, fmtDate, fmtMoney, shortId,
} from '../../components/ui';
import { navigate, useAsync } from '../../hooks';

const CATEGORY_ORDER = [
    'invoices_not_fully_acted',
    'acts_awaiting_signature',
    'originals_to_send',
    'requisite_discrepancies',
    'debts',
    'cancellation_required',
];

const DOC_TYPE_LABELS: Record<string, string> = {
    invoice: 'Счёт',
    act: 'Акт',
    bank_operation: 'Банковская операция',
};

export function FinanceAttentionTab() {
    const attention = useAsync<FinanceAttention>(() => finance.attention(), []);
    const data = attention.data;

    const columns: Column<FinAttentionRow>[] = [
        { key: 'request', title: 'Заявка', render: (r) => r.request ? (
            <a href={`#/requests/${r.request.id}`}>{r.request.number ?? shortId(r.request.id)}</a>
        ) : '—' },
        { key: 'client', title: 'Клиент', render: (r) => r.client?.name ?? '—' },
        { key: 'document', title: 'Документ', render: (r) => (
            <span>{DOC_TYPE_LABELS[r.document.type] ?? r.document.type} {r.document.number ?? shortId(r.document.id)}</span>
        ) },
        { key: 'problem', title: 'Проблема', render: (r) => r.problem },
        { key: 'amount', title: 'Сумма', render: (r) => fmtMoney(r.amount, r.currency) },
        { key: 'due_at', title: 'Срок', render: (r) => fmtDate(r.due_at) },
        { key: 'assignee', title: 'Ответственный', render: (r) => (r.assignee != null ? String(r.assignee) : '—') },
    ];

    const categories = CATEGORY_ORDER
        .map((code) => ({ code, ...(data?.categories?.[code] ?? { label: code, rows: [] as FinAttentionRow[] }) }));

    return (
        <div>
            <ErrorBox error={attention.error} onRetry={attention.reload} />
            {attention.loading && !data ? <div className="loading">Загрузка…</div> : null}
            {data ? (
                <div className="field-hint" style={{ marginBottom: 8 }}>
                    Всего записей: {data.total}. Раздел вычисляется по текущим данным —
                    запись исчезает после устранения причины.
                </div>
            ) : null}
            {categories.map((cat) => (
                <Card key={cat.code} title={`${cat.label} (${cat.rows.length})`}>
                    <DataTable
                        columns={columns}
                        rows={cat.rows}
                        empty="Проблем нет"
                        onRowClick={(r) => {
                            if (r.action_url?.startsWith('#/requests/')) {
                                navigate(r.action_url.slice(1));
                            }
                        }}
                    />
                </Card>
            ))}
        </div>
    );
}
